/**
 * Экран проверки паспортных данных (п. 6.4 ТЗ).
 *
 * Поля предзаполнены OCR; распознанные неуверенно подсвечены жёлтым, чтобы
 * монтажник посмотрел именно на них. Каждое изменение сразу пишется в локальный
 * черновик — при обрыве связи введённое не пропадёт (п. 9 ТЗ).
 *
 * Адрес регистрации выбирается из адресного справочника биллинга (город →
 * улица → дом → квартира), а не набирается строкой: с привязкой к дому его
 * можно одной кнопкой перенести в адрес подключения на следующем шаге и потом
 * записать в карточку абонента без разбора текста.
 *
 * Прописка вне Якутии в справочнике не найдётся никогда, поэтому ввод текстом
 * остаётся равноправным режимом, а не запасным выходом.
 */

import { useEffect, useMemo, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { Application, PassportFields } from '../api/types'
import { AddressPicker } from '../components/AddressPicker'
import type { AddressValue } from '../components/AddressPicker'
import { Alert, Button, Field, Select, Steps } from '../components/ui'
import { clearDraft, loadDraft, saveDraft } from '../lib/draft'
import { digitsOnly, formatSubdivisionInput } from '../lib/format'

const DRAFT_KEY = 'passport'

type Values = Record<keyof PassportFields, string>

const EMPTY: Values = {
  last_name: '',
  first_name: '',
  middle_name: '',
  birth_date: '',
  birth_place: '',
  sex: '',
  passport_series: '',
  passport_number: '',
  issue_date: '',
  issue_authority: '',
  subdivision_code: '',
  registration_address: '',
}

const REQUIRED: (keyof Values)[] = [
  'last_name',
  'first_name',
  'birth_date',
  'birth_place',
  'sex',
  'passport_series',
  'passport_number',
  'issue_date',
  'issue_authority',
  'subdivision_code',
  'registration_address',
]

interface Props {
  application: Application
  onDone: (application: Application) => void
  onBack: () => void
  onRescan: () => void
}

function fromApplication(application: Application): Values {
  const values = { ...EMPTY }
  for (const key of Object.keys(EMPTY) as (keyof Values)[]) {
    values[key] = application[key] ?? ''
  }
  return values
}

function registrationFromApplication(application: Application): AddressValue {
  return {
    city_id: application.registration_city_id,
    street_id: application.registration_street_id,
    house_id: application.registration_house_id,
    flat: application.registration_flat ?? '',
    // Адрес без привязки к справочнику — это ещё не выбранная прописка: строку
    // туда кладёт OCR. Показываем её подсказкой, а не как готовое значение,
    // иначе каскад свернётся и монтажник решит, что адрес уже выбран.
    text: application.registration_city_id ? (application.registration_address ?? '') : '',
  }
}

export function ReviewScreen({ application, onDone, onBack, onRescan }: Props) {
  const draftId = `${application.id}.${DRAFT_KEY}`

  const [values, setValues] = useState<Values>(() => {
    const server = fromApplication(application)
    const draft = loadDraft<Partial<Values>>(draftId)
    // Локальный черновик новее серверной копии: он и появляется только тогда,
    // когда отправка не прошла.
    return draft ? { ...server, ...draft } : server
  })
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>({})
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [saving, setSaving] = useState(false)
  const [restoredDraft] = useState(() => loadDraft<Partial<Values>>(draftId) !== null)

  // ── прописка: только выбор из справочника биллинга ─────────────────────────
  const regDraftId = `${draftId}.registration`
  const [registration, setRegistration] = useState<AddressValue>(() => {
    const server = registrationFromApplication(application)
    const draft = loadDraft<AddressValue>(regDraftId)
    return draft ?? server
  })
  // Что распознал OCR на странице с пропиской. Не значение поля, а подсказка:
  // по ней монтажник находит нужный дом в справочнике.
  const [scannedAddress] = useState(
    () => (application.registration_city_id ? '' : (application.registration_address ?? '')),
  )

  useEffect(() => {
    saveDraft(draftId, values)
  }, [draftId, values])

  useEffect(() => {
    saveDraft(regDraftId, registration)
  }, [regDraftId, registration])

  const lowConfidence = useMemo(
    () => new Set(application.ocr_meta?.low_confidence_fields ?? []),
    [application.ocr_meta],
  )

  const set = (key: keyof Values) => (value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  /** Адрес и его строка ходят парой: строку печатает договор, id — привязка. */
  const setRegistrationAddress = (address: AddressValue) => {
    setRegistration(address)
    set('registration_address')(address.text)
  }

  function validate(): boolean {
    const found: Partial<Record<keyof Values, string>> = {}
    for (const key of REQUIRED) {
      if (!values[key].trim()) found[key] = 'Обязательное поле'
    }
    if (values.passport_series && values.passport_series.length !== 4) {
      found.passport_series = 'Серия — 4 цифры'
    }
    if (values.passport_number && values.passport_number.length !== 6) {
      found.passport_number = 'Номер — 6 цифр'
    }
    if (values.subdivision_code && digitsOnly(values.subdivision_code, 6).length !== 6) {
      found.subdivision_code = 'Код подразделения — 6 цифр'
    }
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async () => {
    if (!validate()) return
    setSaving(true)
    setSaveError(null)
    try {
      const patch = Object.fromEntries(
        (Object.keys(values) as (keyof Values)[]).map((key) => [key, values[key] || null]),
      )
      const updated = await api.patchApplication(application.id, {
        ...patch,
        registration_city_id: registration.city_id,
        registration_street_id: registration.street_id,
        registration_house_id: registration.house_id,
        registration_flat: registration.flat || null,
      })
      // Сервер подтвердил — локальная копия больше не нужна.
      clearDraft(draftId)
      clearDraft(regDraftId)
      onDone(updated)
    } catch (err) {
      setSaveError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
    } finally {
      setSaving(false)
    }
  }

  const lowConfidenceCount = REQUIRED.filter((key) => lowConfidence.has(key)).length

  return (
    <>
      <Steps current={2} />
      <div className="card">
        <h2>Проверьте данные паспорта</h2>
        <p className="subtitle">
          Сверьте с документом. Поля можно править — итоговые значения попадут в договор.
        </p>

        {restoredDraft && (
          <Alert kind="warn" title="Восстановлен черновик">
            Предыдущая отправка не завершилась — данные взяты из локальной копии на
            устройстве. Проверьте их и нажмите «Далее».
          </Alert>
        )}

        {lowConfidenceCount > 0 && (
          <Alert kind="warn" title="Есть неуверенно распознанные поля">
            Жёлтым выделены {lowConfidenceCount} полей — сверьте их с документом особенно
            внимательно.
          </Alert>
        )}

        {saveError && (
          <Alert
            kind="error"
            title={saveError.retryable ? 'Данные не сохранились' : 'Сервер отклонил данные'}
            onRetry={saveError.retryable ? submit : undefined}
            onDismiss={() => setSaveError(null)}
          >
            {saveError.message}
            {saveError.retryable && ' Введённое сохранено на устройстве и не потеряется.'}
          </Alert>
        )}

        <div className="field-row">
          <Field
            name="last_name"
            value={values.last_name}
            onChange={set('last_name')}
            required
            lowConfidence={lowConfidence.has('last_name')}
            error={errors.last_name}
          />
          <Field
            name="first_name"
            value={values.first_name}
            onChange={set('first_name')}
            required
            lowConfidence={lowConfidence.has('first_name')}
            error={errors.first_name}
          />
        </div>

        <Field
          name="middle_name"
          value={values.middle_name}
          onChange={set('middle_name')}
          lowConfidence={lowConfidence.has('middle_name')}
          hint="Если отчества нет в паспорте — оставьте пустым"
        />

        <div className="field-row">
          <Field
            name="birth_date"
            type="date"
            value={values.birth_date}
            onChange={set('birth_date')}
            required
            lowConfidence={lowConfidence.has('birth_date')}
            error={errors.birth_date}
          />
          <Select
            name="sex"
            value={values.sex}
            onChange={set('sex')}
            required
            lowConfidence={lowConfidence.has('sex')}
            error={errors.sex}
            options={[
              { value: 'male', label: 'Мужской' },
              { value: 'female', label: 'Женский' },
            ]}
          />
        </div>

        <Field
          name="birth_place"
          value={values.birth_place}
          onChange={set('birth_place')}
          required
          lowConfidence={lowConfidence.has('birth_place')}
          error={errors.birth_place}
          multiline
        />

        <div className="field-row">
          <Field
            name="passport_series"
            value={values.passport_series}
            onChange={(value) => set('passport_series')(digitsOnly(value, 4))}
            required
            inputMode="numeric"
            placeholder="4509"
            lowConfidence={lowConfidence.has('passport_series')}
            error={errors.passport_series}
          />
          <Field
            name="passport_number"
            value={values.passport_number}
            onChange={(value) => set('passport_number')(digitsOnly(value, 6))}
            required
            inputMode="numeric"
            placeholder="123456"
            lowConfidence={lowConfidence.has('passport_number')}
            error={errors.passport_number}
          />
        </div>

        <div className="field-row">
          <Field
            name="issue_date"
            type="date"
            value={values.issue_date}
            onChange={set('issue_date')}
            required
            lowConfidence={lowConfidence.has('issue_date')}
            error={errors.issue_date}
          />
          <Field
            name="subdivision_code"
            value={values.subdivision_code}
            onChange={(value) => set('subdivision_code')(formatSubdivisionInput(value))}
            required
            inputMode="numeric"
            placeholder="770-001"
            lowConfidence={lowConfidence.has('subdivision_code')}
            error={errors.subdivision_code}
          />
        </div>

        <Field
          name="issue_authority"
          value={values.issue_authority}
          onChange={set('issue_authority')}
          required
          lowConfidence={lowConfidence.has('issue_authority')}
          error={errors.issue_authority}
          multiline
        />

        {/* ── адрес регистрации ────────────────────────────────────────── */}

        {scannedAddress && !registration.text && (
          <Alert kind="warn" title="Прописка со снимка">
            {scannedAddress}
            <div className="hint">
              Распознано со страницы с пропиской. Выберите этот адрес в справочнике
              ниже — в договор пойдёт выбранное значение.
            </div>
          </Alert>
        )}

        <AddressPicker
          label="Шаг 1 из 2 — адрес регистрации (прописка)"
          value={registration}
          onChange={setRegistrationAddress}
          error={errors.registration_address}
          hint="Как в паспорте, на странице с пропиской. Адрес подключения укажете на следующем шаге."
        />

        <div className="actions">
          <Button variant="secondary" onClick={onBack} disabled={saving}>
            Назад
          </Button>
          <Button variant="secondary" onClick={onRescan} disabled={saving}>
            Доснять страницу
          </Button>
          <Button onClick={submit} busy={saving}>
            Далее
          </Button>
        </div>
      </div>
    </>
  )
}
